import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { Editor } from 'react-draft-wysiwyg';
import {
  BED_STATUS_CUBICLE_COLOR,
  BED_STATUS_REMARK_COLOR,
  E_ADMISSION_GRID_SIMENSIONS,
} from '../../../../../comm/constant';
import { classNames, onKeyUpEnterOrSpace } from '../../../../../utils';
import useCheckLogin from '../../../../../utils/useCheckLogin';
import LocCubicleDialog from '../../LocCubicleDialog';
import LandingUpdateCubicleDialog from './LandingUpdateCubicleDialog';

const useStyles = makeStyles(() => ({
  root: ({ color }) => ({
    backgroundColor: BED_STATUS_CUBICLE_COLOR[color] ?? '#fff',
  }),
  name: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#7d86a9',
    fontSize: '1.25rem',
    width: '80%',
    textAlign: 'center',
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.875rem',
    color: '#2e4469',
    lineHeight: '1.05rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  status: {
    backgroundColor: '#979bff',
    padding: '0.125rem 0.25rem',
    borderRadius: '2px',
  },
  statusContent: {
    color: '#fff',
    fontSize: '0.75rem',
    fontFamily: 'NotoSans-Medium',
  },
  clickStyle: {
    '&:hover': {
      cursor: 'default',
    },
  },
  tipBar: ({ remarkColor }) => ({
    backgroundColor: BED_STATUS_REMARK_COLOR[remarkColor]?.bgColor ?? '#fff',
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
    padding: '0.25rem 0.5rem',
    position: 'absolute',
    height: '100%',
    top: 0,
    left: 0,
    right: 0,
    overflow: 'hidden',
    '&::before': {
      position: 'absolute',
      top: '0',
      left: '0',
      content: '""',
      display: 'block',
      width: '0.25rem',
      height: '100%',
      backgroundColor: BED_STATUS_REMARK_COLOR[remarkColor]?.leftColor ?? '#fff',
      zIndex: '9',
    },
  }),
  tips: ({ remarkColor }) => ({
    fontFamily: 'NotoSans-Medium',
    color: BED_STATUS_REMARK_COLOR[remarkColor]?.color ?? '#2e4469',
    fontSize: '0.75rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    display: '-webkit-box',
    whiteSpace: 'pre-wrap',
    '& .public-DraftStyleDefault-block': {
      margin: 'unset',
    },
  }),
  wrapper: {
    // height: '100%',
  },
  editor: {
    overflow: 'inherit',
  },
}));

const EADMemo = ({
  style = {},
  cubicleData,
  id,
  handleUpdateCubicle = null,
  handleDeleteCubicle = null,
  handleCopyCubicle = null,
  editMode = false,
}) => {
  const classes = useStyles({ color: cubicleData.color, remarkColor: cubicleData.remarkColor });
  const remarkRef = useRef();
  const remarkContentRef = useRef();
  const [editOpen, setEditOpen] = useState(false);
  const checkLogin = useCheckLogin();

  useEffect(() => {
    const lineHeight = parseFloat(getComputedStyle(remarkContentRef.current).lineHeight);
    const lines = Math.floor(remarkRef.current.clientHeight / lineHeight) - 1;

    remarkContentRef.current.style.webkitLineClamp = lines;
  }, [remarkRef, cubicleData.bedArrangement]);

  const handleOpenMore = useCallback(() => {
    if (checkLogin()) {
      setEditOpen(true);
    }
  }, [checkLogin, cubicleData]);

  const renderEditPopup = () => {
    const params = {
      handleClose: () => setEditOpen(false),
      showPopup: editOpen,
      cubicleId: id,
      cubicleData: cubicleData,
      handleSubmit: handleUpdateCubicle,
    };

    if (editMode) {
      return (
        <LocCubicleDialog
          {...params}
          operation="edit"
          handleCopyCubicle={handleCopyCubicle}
          handleDeleteCubicle={handleDeleteCubicle}
        />
      );
    }

    return <LandingUpdateCubicleDialog {...params} />;
  };

  return (
    <>
      <Box
        className={classes.root}
        style={style}
        position="relative"
        display="inline-flex"
        minWidth={E_ADMISSION_GRID_SIMENSIONS?.width}
        minHeight={E_ADMISSION_GRID_SIMENSIONS?.height}
        border={cubicleData.borderLine === 'show' ? 'solid 1px #AAAAAA' : 'unset'}
        borderRadius="8px"
        p="0.875rem 0.813rem 0.875rem 0.688rem"
      >
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          position="absolute"
          width="calc(100% - 0.813rem - 0.688rem)"
          top="0.875rem"
        >
          <Box display="flex" alignItems="center">
            <div
              className={classNames(classes.title, classes.clickStyle)}
              onClick={handleOpenMore}
              tabIndex={0}
              onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpenMore)}
            >
              {cubicleData.name}
            </div>
          </Box>
          {cubicleData.status && (
            <Box display="flex" alignItems="center">
              <div
                className={classNames(classes.status, classes.clickStyle)}
                onClick={handleOpenMore}
                tabIndex={0}
                onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpenMore)}
              >
                <div className={classes.statusContent}>{cubicleData.status}</div>
              </div>
            </Box>
          )}
        </Box>
        <Box position="absolute" top="2.36rem" right="0.813rem" left="0.688rem" bottom="0.875rem" marginTop="0.5rem">
          <Box position="relative" height="100%">
            <div
              className={classes.tipBar}
              ref={remarkRef}
              onClick={handleOpenMore}
              tabIndex={0}
              onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleOpenMore)}
            >
              <div className={classes.tips} ref={remarkContentRef}>
                <Editor
                  editorState={cubicleData.remarks}
                  wrapperClassName={classes.wrapper}
                  editorClassName={classes.editor}
                  readOnly // 设置为只读模式，用于显示内容
                  toolbarHidden // 隐藏工具栏
                />
              </div>
            </div>
          </Box>
        </Box>
        {/* 撑开size */}
        <Box display="flex" flexDirection="column">
          <Box height="4.5rem" width="100%" />
          <Box
            display="grid"
            gridTemplateRows={`repeat(${cubicleData.bedArrangement.row}, 1fr)`}
            gridTemplateColumns={`repeat(${cubicleData.bedArrangement.column}, 1fr)`}
            gridGap="0.2rem"
          >
            {cubicleData.beds.map((item) => (
              <Box
                key={item.key}
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={E_ADMISSION_GRID_SIMENSIONS?.width}
                height={E_ADMISSION_GRID_SIMENSIONS?.height}
                pt="1.563rem"
              />
            ))}
          </Box>
        </Box>
      </Box>

      {editOpen && renderEditPopup()}
    </>
  );
};

export default EADMemo;
